import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import {
  statutLabel, statutColor, statutSuivantLocal, typeAnimalLabel, causeLabel,
  fmtEuro, fmtDate, chipPastel,
} from "components/sacrificesConfig";

const defaultForm = {
  beneficiaire_nom: "",
  beneficiaire_prenom: "",
  beneficiaire_telephone: "",
  type_animal: "ovins_caprins",
  nombre: 1,
  cause: "oudhiya",
  option_video: false,
  notes: "",
};

function MesSacrifices() {
  const [session, setSession] = useState(null);
  const [sacrifices, setSacrifices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/sacrifice-sessions/active"), api.get("/sacrifices")]);
      setSession(s.data);
      setSacrifices(l.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const prixUnitaire = session ? parseFloat(session["prix_" + form.type_animal]) || 0 : 0;
  const total = prixUnitaire * (parseInt(form.nombre, 10) || 0);

  const handleOpenNew = () => {
    setEditId(null);
    setForm(defaultForm);
    setError("");
    setOpen(true);
  };

  const handleOpenEdit = (s) => {
    setEditId(s.id);
    setForm({
      beneficiaire_nom: s.beneficiaire_nom,
      beneficiaire_prenom: s.beneficiaire_prenom,
      beneficiaire_telephone: s.beneficiaire_telephone || "",
      type_animal: s.type_animal,
      nombre: s.nombre,
      cause: s.cause,
      option_video: s.option_video,
      notes: s.notes || "",
    });
    setError("");
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!form.beneficiaire_nom || !form.beneficiaire_prenom) {
      setError("Le nom et le prénom du bénéficiaire sont obligatoires");
      return;
    }
    if (!(parseInt(form.nombre, 10) > 0)) {
      setError("Le nombre doit être supérieur à 0");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = { ...form, nombre: parseInt(form.nombre, 10) };
      if (editId) {
        await api.put("/sacrifices/" + editId, payload);
      } else {
        await api.post("/sacrifices", payload);
      }
      fetchAll();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  const avancerStatut = async (s) => {
    const suivant = statutSuivantLocal[s.statut];
    if (!window.confirm(`Passer la demande au statut "${statutLabel[suivant.statut]}" ?\nCette action est définitive.`)) return;
    try {
      await api.put("/sacrifices/" + s.id + "/statut", { statut: suivant.statut });
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur serveur");
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer la demande de ${s.beneficiaire_prenom} ${s.beneficiaire_nom} ?`)) return;
    try {
      await api.delete("/sacrifices/" + s.id);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur serveur");
    }
  };

  const tuilePrix = (label, valeur) => (
    <Grid item xs={4}>
      <MDBox sx={{ bgcolor: "#f8f9fa", borderRadius: "8px", p: 1.5, textAlign: "center" }}>
        <MDTypography variant="caption" color="text" display="block">{label}</MDTypography>
        <MDTypography variant="h6" color="info">{fmtEuro(valeur)}</MDTypography>
      </MDBox>
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDTypography variant="h6" color="white">Mes sacrifices</MDTypography>
                <MDButton variant="outlined" color="white" size="small" onClick={handleOpenNew} disabled={!session}>
                  <Icon>add</Icon>&nbsp;Nouvelle demande
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3}>
                {session ? (
                  <MDBox sx={{ border: "1px solid #c8e6c9", bgcolor: "#43a04710", borderRadius: "8px", p: 2 }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <MDTypography variant="h6">{session.libelle}</MDTypography>
                      <Chip label="Session active" size="small" sx={chipPastel("#43a047")} />
                    </MDBox>
                    <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                      Du {fmtDate(session.date_debut)} au {fmtDate(session.date_fin)}
                    </MDTypography>
                    <Grid container spacing={1.5}>
                      {tuilePrix("Ovins / Caprins", session.prix_ovins_caprins)}
                      {tuilePrix("Vache", session.prix_vache)}
                      {tuilePrix("Chameau", session.prix_chameau)}
                    </Grid>
                  </MDBox>
                ) : (
                  <MDBox sx={{ border: "1px solid #eee", bgcolor: "#f8f9fa", borderRadius: "8px", p: 2, textAlign: "center" }}>
                    <MDTypography variant="button" color="text">
                      <Icon sx={{ verticalAlign: "middle", mr: 0.5 }}>info</Icon>
                      Aucune session active : la saisie de nouvelles demandes est fermée.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={2}>
                  {sacrifices.map((s) => {
                    const suivant = statutSuivantLocal[s.statut];
                    return (
                      <Grid item xs={12} md={6} lg={4} key={s.id}>
                        <Card sx={{ p: 2, border: "1px solid #eee", height: "100%" }}>
                          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <MDTypography variant="h6">
                              {s.beneficiaire_prenom} {s.beneficiaire_nom.toUpperCase()}
                            </MDTypography>
                            <Chip label={statutLabel[s.statut]} size="small" sx={chipPastel(statutColor[s.statut])} />
                          </MDBox>
                          <MDBox display="flex" gap={0.75} flexWrap="wrap" mb={1}>
                            <Chip size="small" label={`${s.nombre} × ${typeAnimalLabel[s.type_animal]}`} sx={chipPastel("#1976d2")} />
                            <Chip size="small" label={causeLabel[s.cause]} sx={chipPastel("#7b809a")} />
                            {s.option_video && <Chip size="small" label="📹 Vidéo" sx={chipPastel("#e91e63")} />}
                          </MDBox>
                          {s.beneficiaire_telephone && (
                            <MDTypography variant="caption" color="text" display="block">
                              Tél. : {s.beneficiaire_telephone}
                            </MDTypography>
                          )}
                          {s.saisi_par_nom && (
                            <MDTypography variant="caption" color="text" display="block">
                              Saisi par : {s.saisi_par_nom}
                            </MDTypography>
                          )}
                          <MDTypography variant="caption" color="text" display="block">
                            {s.session_libelle} · {fmtDate(s.created_at)}
                          </MDTypography>
                          {s.notes && (
                            <MDTypography variant="caption" color="text" display="block" sx={{ fontStyle: "italic", whiteSpace: "pre-wrap" }}>
                              {s.notes}
                            </MDTypography>
                          )}
                          <MDTypography variant="button" fontWeight="bold" color="info" display="block" mt={1}>
                            Total : {fmtEuro(s.total_calcule)}
                          </MDTypography>

                          <MDBox mt={1.5} display="flex" flexDirection="column" gap={1}>
                            {suivant && (
                              <MDButton size="small" color="info" fullWidth onClick={() => avancerStatut(s)}>
                                <Icon>{suivant.icon}</Icon>&nbsp;{suivant.label}
                              </MDButton>
                            )}
                            {s.statut === "en_attente" && (
                              <MDBox display="flex" gap={1}>
                                <MDButton size="small" color="info" variant="outlined" fullWidth onClick={() => handleOpenEdit(s)}>
                                  <Icon>edit</Icon>&nbsp;Modifier
                                </MDButton>
                                <MDButton size="small" color="error" variant="outlined" fullWidth onClick={() => handleDelete(s)}>
                                  <Icon>delete</Icon>&nbsp;Supprimer
                                </MDButton>
                              </MDBox>
                            )}
                          </MDBox>
                        </Card>
                      </Grid>
                    );
                  })}
                  {sacrifices.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune demande.{session ? " Cliquez sur \"Nouvelle demande\"." : ""}
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Modifier la demande" : "Nouvelle demande de sacrifice"}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <MDTypography variant="overline" color="text" fontWeight="bold">Bénéficiaire</MDTypography>
            <MDBox display="flex" gap={2}>
              <TextField
                label="Nom" fullWidth
                value={form.beneficiaire_nom}
                onChange={(e) => setForm({ ...form, beneficiaire_nom: e.target.value })}
              />
              <TextField
                label="Prénom" fullWidth
                value={form.beneficiaire_prenom}
                onChange={(e) => setForm({ ...form, beneficiaire_prenom: e.target.value })}
              />
            </MDBox>
            <TextField
              label="Téléphone" fullWidth
              value={form.beneficiaire_telephone}
              onChange={(e) => setForm({ ...form, beneficiaire_telephone: e.target.value })}
            />

            <MDTypography variant="overline" color="text" fontWeight="bold">Sacrifice</MDTypography>
            <MDBox display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel id="type-animal-label">Type d'animal</InputLabel>
                <Select
                  labelId="type-animal-label" label="Type d'animal" sx={{ height: 44 }}
                  value={form.type_animal}
                  onChange={(e) => setForm({ ...form, type_animal: e.target.value })}
                >
                  {Object.entries(typeAnimalLabel).map(([v, l]) => (
                    <MenuItem key={v} value={v}>{l}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Nombre" type="number" sx={{ width: 120 }}
                inputProps={{ min: 1, step: 1 }}
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </MDBox>
            <MDBox display="flex" justifyContent="space-between" sx={{ bgcolor: "#f8f9fa", borderRadius: "8px", p: 1.5 }}>
              <MDTypography variant="button" color="text">
                Prix unitaire : <b>{fmtEuro(prixUnitaire)}</b>
              </MDTypography>
              <MDTypography variant="button" fontWeight="bold" color="info">
                Total : {fmtEuro(total)}
              </MDTypography>
            </MDBox>
            <FormControl fullWidth>
              <InputLabel id="cause-label">Cause</InputLabel>
              <Select
                labelId="cause-label" label="Cause" sx={{ height: 44 }}
                value={form.cause}
                onChange={(e) => setForm({ ...form, cause: e.target.value })}
              >
                {Object.entries(causeLabel).map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.option_video}
                  onChange={(e) => setForm({ ...form, option_video: e.target.checked })}
                />
              )}
              label={<MDTypography variant="button">📹 Option vidéo</MDTypography>}
            />
            <TextField
              label="Notes (optionnel)" fullWidth multiline rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            {error && (
              <MDTypography variant="caption" color="error">{error}</MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary">Annuler</MDButton>
          <MDButton onClick={handleSubmit} color="info" disabled={loading}>
            {loading ? "Enregistrement..." : editId ? "Modifier" : "Créer"}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default MesSacrifices;
